import type { RunStatus } from './types';
import { RUN_HEARTBEAT_STALE_AFTER_MS, RUN_NO_HEARTBEAT_GRACE_MS } from './types';

export const RUN_POLL_INTERVAL_MS = 5_000;

/**
 * Re-exported from the shared contract so callers only need one import — the same window the
 * backend reports `running`/orphaned at either side of the wire.
 */
export { RUN_HEARTBEAT_STALE_AFTER_MS, RUN_NO_HEARTBEAT_GRACE_MS };

/**
 * The structural slice of a run (or a run summary) its liveness depends on — a `RunSummary`
 * satisfies this too, so the picker can apply the same rule as the page without widening its own
 * type. `heartbeatAt` absent means "no heartbeat yet" (queued, or pre-worker), not "dead".
 */
export interface RunLiveness {
  status?: RunStatus;
  generatedAt: string;
  heartbeatAt?: string;
}

/**
 * The clock is `heartbeatAt ?? generatedAt`, and the budget depends on which was used — mirrors the
 * backend's own rule, so a run is never called dead on one side of the wire while the other still
 * reports it running.
 *
 * A run with a heartbeat is judged by how long it has been silent (5 min — comfortably more than the
 * slowest single turn). A run with none yet is judged by how long ago it was reported started,
 * bounded by a wider grace window so a slow queue never kills a run at trigger time.
 */
const isStale = (run: RunLiveness, now: number): boolean => {
  const clock = run.heartbeatAt ?? run.generatedAt;
  const clockMs = Date.parse(clock);
  if (Number.isNaN(clockMs)) return true;
  const budget =
    run.heartbeatAt !== undefined ? RUN_HEARTBEAT_STALE_AFTER_MS : RUN_NO_HEARTBEAT_GRACE_MS;
  return now - clockMs > budget;
};

/**
 * How long until a run would age out, for scheduling the re-render that notices.
 *
 * Nothing else will trigger it: the stored stub is identical on every poll, so react-query's
 * structural sharing keeps the same object and never notifies, and a status computed during render
 * would go on reporting `running` long after the window closed.
 */
export const msUntilStale = (run: RunLiveness, now: number): number => {
  const clock = run.heartbeatAt ?? run.generatedAt;
  const clockMs = Date.parse(clock);
  if (Number.isNaN(clockMs)) return 0;
  const budget =
    run.heartbeatAt !== undefined ? RUN_HEARTBEAT_STALE_AFTER_MS : RUN_NO_HEARTBEAT_GRACE_MS;
  return Math.max(clockMs + budget - now, 0);
};

/**
 * What to show, as opposed to what to act on. `queued` exists only here — nothing acts on it, so a
 * queued run still holds the loop's interlock and still ages out to `failed` on the same clock.
 */
export type RunPresentation = RunStatus | 'queued';

/**
 * Non-zero on purpose: a manual trigger reaches a worker in about a second, so without a threshold
 * the copy would flip backwards for a moment on every click.
 */
export const RUN_QUEUED_AFTER_MS = 60 * 1000;

export const presentRunStatus = (
  status: RunStatus,
  run: RunLiveness,
  now: number,
): RunPresentation =>
  status === 'running' &&
  run.heartbeatAt === undefined &&
  now - Date.parse(run.generatedAt) > RUN_QUEUED_AFTER_MS
    ? 'queued'
    : status;

/**
 * How long until a run with no heartbeat crosses into `queued`, for scheduling the re-render that
 * notices — see `msUntilStale`. Unclamped, unlike that one: a caller arming a timer has to tell
 * "not yet" from "already past", which clamping to zero would collapse.
 */
export const msUntilQueued = (run: RunLiveness, now: number): number => {
  const generatedMs = Date.parse(run.generatedAt);
  if (Number.isNaN(generatedMs)) return 0;
  return generatedMs + RUN_QUEUED_AFTER_MS - now;
};

/**
 * The status to act on, as opposed to the one the backend last wrote. An absent status belongs to a
 * run stored before background execution, which is always finished.
 */
export const resolveEffectiveRunStatus = (run: RunLiveness, now: number): RunStatus => {
  if (run.status === undefined) return 'completed';
  if (run.status === 'running' && isStale(run, now)) return 'failed';
  return run.status;
};

/**
 * Only a `running` run is worth re-reading, and only while it is still plausibly alive.
 *
 * Read failures deliberately do not stop the poll. react-query keeps serving the last successful data
 * through them, so the staleness check still applies and bounds the poll on its own — while the
 * retries keep going, so a run survives sir restarting underneath it and resumes on its own once sir
 * is back. Stopping on failure instead would turn every brief restart into a manual retry.
 */
export const resolveRunPollInterval = (
  run: RunLiveness | null | undefined,
  now: number,
): number | false => {
  if (!run) return false;
  if (run.status !== 'running') return false;
  if (isStale(run, now)) return false;
  return RUN_POLL_INTERVAL_MS;
};

/** For callers with no hook to hang a timer on. */
export const resolveRunPresentation = (run: RunLiveness, now: number): RunPresentation =>
  presentRunStatus(resolveEffectiveRunStatus(run, now), run, now);
